import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { getActiveLocaleSelector } from '@americanexpress/travel-selectors';
import { locales } from '@americanexpress/conditional-features';
import clsx from 'clsx';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DEFAULT_DATE_FORMAT } from '../../constants/dateFormatConstants';
import { getHasTimeDropdown, getIs24Hour } from '../../selectors/timeSelectors';
import { formatHeading } from '../../utils/dateUtils';
import styles from './InputMasks.scss';
import { useTimeSelector } from '../../context/timeContext';
import ANYTIME from '../../constants/timeDropdownConstants';

export const generateDateForEsMx = (locale, luxonDate, timeZone) => {
  const day = formatHeading(locale, luxonDate, { day: 'numeric', timeZone });
  const month = formatHeading(locale, luxonDate, { month: 'short', timeZone });
  const weekday = formatHeading(locale, luxonDate, { weekday: 'short', timeZone });
  return `${weekday}, ${day} ${month}`;
};

export const useDateHeadings = ({ stringDate, time, timeZone }) => {
  const locale = useSelector(getActiveLocaleSelector);
  const hasTimeDropdown = useTimeSelector(getHasTimeDropdown);
  const is24Hour = useTimeSelector(getIs24Hour);

  const luxonDate = useMemo(() => (
    stringDate ? DateTime.fromFormat(stringDate, DEFAULT_DATE_FORMAT) : null
  ), [stringDate]);

  return useMemo(() => {
    let nextHeading;
    let nextSubHeading;
    if (luxonDate) {
      if (hasTimeDropdown && time && time !== ANYTIME) {
        const [hour, minutes] = time.split(':');
        nextHeading = is24Hour
          ? time
          : formatHeading(locale, luxonDate.set({
            hour: Number(hour),
            minute: Number(minutes),
          }), {
            hour: 'numeric',
            minute: 'numeric',
            timeZone,
          });
        if (locale === locales.ES_MX) {
          nextSubHeading = generateDateForEsMx(locale, luxonDate, timeZone);
        } else {
          nextSubHeading = formatHeading(locale, luxonDate, {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            timeZone,
          });
        }
      } else {
        nextHeading = formatHeading(locale, luxonDate, {
          weekday: 'short',
          month: 'short',
          day: 'numeric',
          timeZone,
        });
      }
    }
    return {
      heading: nextHeading,
      subHeading: nextSubHeading,
    };
  }, [luxonDate, time, hasTimeDropdown, is24Hour, locale, timeZone]);
};

const DateInputMask = ({
  stringDate,
  time,
  timeZone,
  timeId,
  dateId,
}) => {
  const {
    heading, subHeading,
  } = useDateHeadings({
    stringDate, time, timeZone,
  });

  return (
    <div className={clsx(
      'flex flex-align-center position-relative width-full display-block',
      styles.fakeInputWrapper)}
    >
      { heading && (
      <span className="automation-date-picker-input-mask-heading text-align-left">
        <span id={timeId}>{heading}</span>
        <span id={dateId}> {subHeading} </span>
      </span>
      )}
    </div>
  );
};

DateInputMask.propTypes = {
  stringDate: PropTypes.string,
  time: PropTypes.string,
  timeZone: PropTypes.string,
  timeId: PropTypes.string,
  dateId: PropTypes.string,
};

DateInputMask.defaultProps = {
  stringDate: null,
  time: null,
  timeZone: undefined,
};

export default DateInputMask;
